import React, {useRef, useState} from "react";
import ReactGA from "react-ga4";
import {FormattedMessage} from "react-intl";
import {useParams} from "react-router-dom";
import {ReactComponent as CloseButtonIcon} from "../image/close_button.svg";
import {cancelStop, RecipientTrackingPageParams, StopCancelReasonsInfo} from "../model/rideInfo";
import {closeModal, openModal} from "../utils/dialog";
import "../styles/Dialog.css";
import {ErrorAlert} from "./ErrorAlert";
import "../styles/RadioInputs.css";
import {SuccessDialog} from "./SuccessDialog";
import {ReactComponent as CancelLegIcon} from "../image/cancel-leg-icon.svg";
import {ReactComponent as RedirectIcon} from "../image/redirect-icon.svg";
import {useQueryClient} from "@tanstack/react-query";

interface StopCancelProps {
    cancelReasons: StopCancelReasonsInfo[];
    redirectToPudoEnabled: boolean;
    onStatusChange: (isOpen: boolean) => void;
    ordererName?: string;
    senderName?: string;
}

export function StopCancel({
    cancelReasons,
    redirectToPudoEnabled,
    onStatusChange,
    ordererName,
    senderName,
}: StopCancelProps) {
    type ActionType = "REDIRECT_TO_PUDO" | "CANCEL_DELIVERY" | null;
    const queryClient = useQueryClient();

    const partnerName = ordererName ?? senderName ?? "";

    const syncClosedState = () => {
        onStatusChange(false);
        setCancelled(false);
    };

    const handleOpen = () => {
        onStatusChange(true);
        openModal(ref.current);
    };

    const handleClose = () => {
        closeModal(ref.current);
    };

    const handleConfirmSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["delivery-stop"]});
        handleClose();
    };

    const [selectedAction, setSelectedAction] = useState<ActionType>(
        redirectToPudoEnabled ? null : "CANCEL_DELIVERY"
    );
    const [showReasons, setShowReasons] = useState(false);
    const reasonsVisible = !redirectToPudoEnabled || showReasons;

    const isChangeMode = redirectToPudoEnabled;

    const ui = {
        buttonClass: isChangeMode ? "secondary-button" : "destructive unavailable-button",
        buttonLabel: isChangeMode ? "change_delivery_intent" : "cancel_delivery_intent",
        title: isChangeMode ? "change_title_question" : "cancel_title_question",
    };

    const isCancelAction = selectedAction === "CANCEL_DELIVERY";

    const confirmLabel = isCancelAction ? "cancel_delivery" : "confirm_redirect_to_pudo";

    const confirmClass = isCancelAction ? "destructive-button" : "primary-button";

    const {shareKey} = useParams<RecipientTrackingPageParams>();
    const [reason, setReason] = useState("");
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    const ref = useRef<HTMLDialogElement>(null);
    const errorMessage = reason ? (
        <FormattedMessage id="cancel_failed" />
    ) : (
        <FormattedMessage id="select_the_reason" />
    );

    const redirectReason = cancelReasons.find((r) => r.reason === "REDIRECT_TO_PUDO");

    const otherReasons = cancelReasons.filter((r) => r.reason !== "REDIRECT_TO_PUDO");

    const submitCancelStop = () => {
        if (!reason) {
            setError(true);
            return;
        }

        setLoading(true);

        cancelStop(shareKey, reason)
            .then(() => {
                setCancelled(true);

                ReactGA.event({
                    category: shareKey,
                    action: "Leg cancel success",
                });
            })
            .catch(() => {
                setError(true);

                ReactGA.event({
                    category: shareKey,
                    action: "Leg cancel fail",
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <button
                className={`stop-action ${ui.buttonClass}`}
                onClick={handleOpen}
                data-cy="cancel-leg-btn"
            >
                <CancelLegIcon />
                <FormattedMessage id={ui.buttonLabel} />
            </button>
            <dialog className="modal" ref={ref} onClose={syncClosedState} aria-labelledby="title">
                {cancelled ? (
                    <SuccessDialog
                        titleId={reason === "REDIRECT_TO_PUDO" ? "redirected_to_pudo" : "cancelled"}
                        descriptionId={
                            reason === "REDIRECT_TO_PUDO"
                                ? "redirected_to_pudo_info"
                                : "cancelled_info"
                        }
                        buttonId="understand"
                        values={{ordererName: partnerName}}
                        onConfirm={handleConfirmSuccess}
                    />
                ) : (
                    <div>
                        <div className="header">
                            <p id="title" className="title">
                                <FormattedMessage id={ui.title} />
                            </p>
                            <button className="close-button" onClick={handleClose}>
                                <CloseButtonIcon />
                            </button>
                        </div>
                        <div>
                            {redirectReason && (
                                <label
                                    className={`pudo-card ${
                                        selectedAction === "REDIRECT_TO_PUDO" ? "selected" : ""
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="cancel-reason"
                                        value={redirectReason.reason}
                                        checked={reason === redirectReason.reason}
                                        onChange={() => {
                                            setSelectedAction("REDIRECT_TO_PUDO");
                                            setReason("REDIRECT_TO_PUDO");
                                            setShowReasons(false);
                                        }}
                                        hidden
                                    />

                                    <div className="pudo-icon">
                                        <RedirectIcon />
                                    </div>

                                    <div className="pudo-content">
                                        <div className="title">{redirectReason.title}</div>
                                        <div className="pudo-description">
                                            <FormattedMessage
                                                id="redirect_info"
                                                values={{ordererName: partnerName}}
                                            />
                                        </div>
                                    </div>
                                </label>
                            )}
                            {redirectToPudoEnabled && (
                                <button
                                    type="button"
                                    className={`redirect-destructive-button ${
                                        selectedAction === "CANCEL_DELIVERY" ? "selected" : ""
                                    }`}
                                    onClick={() => {
                                        setSelectedAction("CANCEL_DELIVERY");
                                        setShowReasons(true);
                                        setReason("");
                                    }}
                                >
                                    <span
                                        className={`redirect-destructive-button-icon ${
                                            showReasons ? "selected" : ""
                                        }`}
                                    >
                                        <CloseButtonIcon />
                                    </span>
                                    <div className="pudo-content">
                                        <div className="title">
                                            <FormattedMessage id="cancel_delivery" />
                                        </div>
                                    </div>
                                </button>
                            )}

                            {selectedAction === "CANCEL_DELIVERY" && reasonsVisible && (
                                <div className="pills-container">
                                    <p className="pills-title">
                                        <FormattedMessage id="select_the_reason" />
                                    </p>
                                    <div className="pills">
                                        {otherReasons.map((reasonOption) => {
                                            const isSelected = reason === reasonOption.reason;
                                            return (
                                                <button
                                                    key={reasonOption.reason}
                                                    type="button"
                                                    className={`pill ${isSelected ? "selected" : ""}`}
                                                    data-cy="cancel-reason"
                                                    onClick={() => {
                                                        setReason(reasonOption.reason);
                                                        setError(false);
                                                    }}
                                                    aria-pressed={isSelected}
                                                >
                                                    {reasonOption.title}
                                                </button>
                                            );
                                        })}
                                    </div>{" "}
                                    {/* TODO: add comment when selecting reason OTHER */}
                                </div>
                            )}
                        </div>

                        {error && <ErrorAlert>{errorMessage}</ErrorAlert>}
                        <button
                            data-cy="confirm-cancel-reason-btn"
                            onClick={submitCancelStop}
                            className={confirmClass}
                            disabled={loading || !reason}
                        >
                            <FormattedMessage id={confirmLabel} />
                        </button>
                    </div>
                )}
            </dialog>
        </>
    );
}
